import React, { useState, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  FormGroup,
  Label,
  Input,
  Table,
  Badge,
  Alert,
} from "reactstrap";

const GPAToPercentageCalculator = () => {
  const [gpa, setGpa] = useState("");

  const handleChange = (value) => {
    if (value === "") {
      setGpa("");
      return;
    }
    const num = Number(value);
    if (num >= 0 && num <= 4) {
      setGpa(value);
    }
  };

  const percentage = useMemo(() => {
    if (gpa === "") return null;
    return ((Number(gpa) / 4) * 100).toFixed(2);
  }, [gpa]);

  const getCategory = (percent) => {
    if (!percent) return { text: "Enter GPA", color: "secondary" };
    if (percent >= 90) return { text: "Excellent", color: "success" };
    if (percent >= 80) return { text: "Very Good", color: "primary" };
    if (percent >= 70) return { text: "Good", color: "info" };
    if (percent >= 60) return { text: "Average", color: "warning" };
    return { text: "Below Average", color: "danger" };
  };

  const category = getCategory(percentage);

  const referenceTable = [
    { gpa: "4.0", percentage: "100%" },
    { gpa: "3.6", percentage: "90%" },
    { gpa: "3.2", percentage: "80%" },
    { gpa: "2.8", percentage: "70%" },
    { gpa: "2.4", percentage: "60%" },
    { gpa: "2.0", percentage: "50%" },
  ];

  return (
    <div style={{ background: "#f8f9fa", minHeight: "100vh", padding: "2rem 0" }}>
      <Container style={{ maxWidth: "900px" }}>

        {/* Header */}
        <Card className="border-0 shadow-sm mb-4">
          <CardBody
            className="text-center py-4"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            }}
          >
            <h1 className="text-white fw-bold mb-2">
              🎓 GPA to Percentage Calculator
            </h1>
            <p className="text-white mb-0" style={{ opacity: 0.9 }}>
              Convert your GPA into percentage easily
            </p>
          </CardBody>
        </Card>

        {/* Input Section */}
        <Card className="border-0 shadow-sm mb-4">
          <CardBody className="p-4">
            <Label className="fw-bold text-uppercase text-muted mb-3">
              📝 Enter GPA
            </Label>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label className="fw-semibold">GPA (out of 4.0)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="0 – 4.0"
                    value={gpa}
                    onChange={(e) => handleChange(e.target.value)}
                    className="py-2"
                  />
                </FormGroup>
              </Col>
            </Row>

            <div className="mt-3 p-3 bg-light rounded border-start border-4 border-warning">
              <small className="text-muted">
                Percentage is calculated using the standard GPA to percentage
                conversion formula.
              </small>
            </div>
          </CardBody>
        </Card>

        {/* Result Section */}
        <Card className="border-0 shadow-sm mb-4">
          <CardBody className="p-4">
            <Label className="fw-bold text-uppercase text-muted mb-3">
              Your Result
            </Label>

            <Alert
              color={category.color}
              className="border-0 mb-0"
              style={{ background: percentage ? undefined : "#e9ecef" }}
            >
              <Row className="align-items-center">
                <Col md={6} className="text-center text-md-start mb-3 mb-md-0">
                  <div className="text-uppercase fw-semibold mb-1 small">
                    Percentage
                  </div>
                  <div style={{ fontSize: "2.5rem", fontWeight: "700" }}>
                    {percentage ?? "0.00"}%
                  </div>
                  <Badge color={category.color} className="mt-2">
                    {category.text}
                  </Badge>
                </Col>

                <Col md={6}>
                  <div className="text-center bg-white rounded p-3">
                    <div className="text-muted small">GPA Scale</div>
                    <div className="fw-bold fs-5">4.0</div>
                  </div>
                </Col>
              </Row>
            </Alert>
          </CardBody>
        </Card>

        {/* Reference Table */}
        <Card className="border-0 shadow-sm">
          <CardBody className="p-4">
            <Label className="fw-bold text-uppercase text-muted mb-3">
              📋 GPA Conversion Reference
            </Label>

            <div className="table-responsive">
              <Table hover className="align-middle mb-0">
                <thead className="bg-light">
                  <tr>
                    <th>GPA</th>
                    <th className="text-end">Percentage</th>
                  </tr>
                </thead>
                <tbody>
                  {referenceTable.map((row, i) => (
                    <tr key={i}>
                      <td className="fw-bold">{row.gpa}</td>
                      <td className="text-end text-muted">
                        {row.percentage}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card>

        {/* Footer */}
        <div className="text-center mt-4">
          <p className="text-muted small mb-0">
            <strong>GPA Calculator</strong> • Standard 4.0 Scale
          </p>
        </div>

      </Container>
    </div>
  );
};

export default GPAToPercentageCalculator;
